import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { Action, combineReducers, configureStore, Middleware } from '@reduxjs/toolkit';

import { SliceNameEnum } from '@app/enums/slice-name.enum';
import { environment } from '@app/environments/environment';
import { modalsMiddleware } from '@app/state/middlewares/modals-middleware';
import { searchUrlMiddleware } from '@app/state/middlewares/search-url.middleware';
import { uiStateSlice } from '@app/state/slices/common-ui-state.slice';
import { listingStateSlice } from '@app/state/slices/listing-state.slice';
import { slice as mapAccommodationStateSlice } from '@app/state/slices/map-accommodation.slice';
import { mapStateSlice } from '@app/state/slices/map-state.slice';
import { searchStateSlice } from '@app/state/slices/search-state.slice';
import { slice as userProfileStateSlice } from '@app/state/slices/user-profile.slice';

const reducers = combineReducers({
    [SliceNameEnum.CommonUiState]: uiStateSlice.reducer,
    [SliceNameEnum.Listing]: listingStateSlice.reducer,
    [SliceNameEnum.Map]: mapStateSlice.reducer,
    [SliceNameEnum.MapAccommodation]: mapAccommodationStateSlice.reducer,
    [SliceNameEnum.Search]: searchStateSlice.reducer,
    [SliceNameEnum.UserProfile]: userProfileStateSlice.reducer,
});

export type AppState = ReturnType<typeof reducers>;

const makeStore = ({
    preloadedState = undefined,
    reduxWrapperMiddleware = undefined,
}: {
    preloadedState?: Partial<AppState>;
    reduxWrapperMiddleware?: Middleware;
}) =>
    configureStore({
        preloadedState: preloadedState ?? undefined,
        devTools: environment.REDUX_DEV_TOOLS
            ? {
                  name: process.env.STORE_NAME ?? 'NOCOWANIE-APP-STORE',
              }
            : false,
        reducer: reducers,
        middleware: getDefaultMiddleware =>
            reduxWrapperMiddleware
                ? getDefaultMiddleware()
                      .concat(reduxWrapperMiddleware)
                      .concat(modalsMiddleware, searchUrlMiddleware)
                : getDefaultMiddleware().concat(modalsMiddleware, searchUrlMiddleware),
    });

type AppStore = ReturnType<typeof makeStore>;
type AppStateRoot = ReturnType<AppStore['getState']>;
export type AppDispatch = AppStore['dispatch'];
export interface ActionWithPayload extends Action {
    type: string;
    payload?: any;
}

const useAppDispatch: () => AppDispatch = useDispatch;
const useAppSelector: TypedUseSelectorHook<AppStateRoot> = useSelector;

export { reducers, makeStore, useAppDispatch, useAppSelector };
export type { AppStore, AppStateRoot };
